/**
 * Authentication Context Provider
 * Provides auth state, credits, and methods to the entire app
 */
import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react'
import { Session } from '@supabase/supabase-js'
import authService, { AuthUser, isAuthConfigured } from '../services/authService'

interface AuthContextType {
  user: AuthUser | null
  session: Session | null
  credits: number | null
  isLoading: boolean
  isConfigured: boolean
  signInWithGoogle: () => Promise<void>
  signInWithGithub: () => Promise<void>
  signOut: () => Promise<void>
  getAccessToken: () => Promise<string | null>
  refreshCredits: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
  children: ReactNode
}

// Mock user for local development — set VITE_MOCK_AUTH=true in .env
const MOCK_AUTH = import.meta.env.VITE_MOCK_AUTH === 'true'
const MOCK_USER: AuthUser = {
  id: 'mock-dev-user',
  email: 'dev@syntaxlens.local',
  displayName: 'Dev User',
  avatarUrl: null,
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(MOCK_AUTH ? MOCK_USER : null)
  const [session, setSession] = useState<Session | null>(null)
  const [credits, setCredits] = useState<number | null>(MOCK_AUTH ? 100 : null)
  const [isLoading, setIsLoading] = useState(MOCK_AUTH ? false : true)
  const syncedRef = useRef(false) // Track if initial sync has been done

  const isConfigured = isAuthConfigured()

  // Short-circuit everything when mocking auth
  if (MOCK_AUTH) {
    return (
      <AuthContext.Provider
        value={{
          user: MOCK_USER,
          session: null,
          credits: 100,
          isLoading: false,
          isConfigured: true,
          signInWithGoogle: async () => { console.log('[MockAuth] signInWithGoogle called') },
          signInWithGithub: async () => { console.log('[MockAuth] signInWithGithub called') },
          signOut: async () => { setUser(null); setCredits(null) },
          getAccessToken: async () => 'mock-token',
          refreshCredits: async () => {},
        }}
      >
        {children}
      </AuthContext.Provider>
    )
  }

  // Fetch credits from backend
  const fetchCredits = useCallback(async () => {
    if (!session) {
      setCredits(null)
      return
    }

    try {
      const token = await authService.getAccessToken()
      if (!token) {
        setCredits(null)
        return
      }

      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'
      const response = await fetch(`${baseUrl}/api/users/me/credits`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setCredits(data.credits)
      } else {
        console.warn('[Auth] Failed to fetch credits:', response.status)
      }
    } catch (error) {
      console.warn('[Auth] Failed to fetch credits:', error)
    }
  }, [session])

  // Public method to refresh credits (call after generation)
  const refreshCredits = useCallback(async () => {
    await fetchCredits()
  }, [fetchCredits])

  useEffect(() => {
    if (!isConfigured) {
      setIsLoading(false)
      return
    }

    // Get initial session
    authService.getSession().then(currentSession => {
      if (currentSession) {
        setSession(currentSession)
        authService.getCurrentUser().then(setUser)
        // Sync user profile on initial load (only once)
        if (!syncedRef.current) {
          syncedRef.current = true
          authService.syncUserProfile(currentSession).catch((error: unknown) => {
            console.warn('[Auth] Failed to sync user profile:', error)
          })
        }
      }
      setIsLoading(false)
    })

    // Subscribe to auth changes
    const { unsubscribe } = authService.onAuthStateChange((authUser, authSession) => {
      const wasLoggedOut = !session && authSession
      setUser(authUser)
      setSession(authSession)

      // Only sync on new login (not on initial load which already synced)
      if (authSession && wasLoggedOut) {
        authService.syncUserProfile(authSession).catch((error: unknown) => {
          console.warn('[Auth] Failed to sync user profile:', error)
        })

        // Clean up auth callback URL after successful login
        if (window.location.pathname === '/auth/callback') {
          window.history.replaceState({}, '', '/')
        }
      }

      // Clear credits on logout
      if (!authSession) {
        setCredits(null)
      }
    })

    return () => unsubscribe()
  }, [isConfigured])

  // Fetch credits when session changes
  useEffect(() => {
    if (session) {
      fetchCredits()
    }
  }, [session, fetchCredits])

  const signInWithGoogle = async () => {
    await authService.signInWithProvider('google')
  }

  const signInWithGithub = async () => {
    await authService.signInWithProvider('github')
  }

  const signOut = async () => {
    await authService.signOut()
    setUser(null)
    setSession(null)
    setCredits(null)
  }

  const getAccessToken = async () => {
    return authService.getAccessToken()
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        credits,
        isLoading,
        isConfigured,
        signInWithGoogle,
        signInWithGithub,
        signOut,
        getAccessToken,
        refreshCredits
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
